import React, { Fragment, ChangeEvent } from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import {
  ButtonPrimary,
  ButtonTertiary,
  DialogFullScreen,
  NavStepper,
  NavStepperItem,
} from '@spotify-internal/encore-creator-web';
import { ClaimPodcast } from '../ClaimPodcast';
import { RSSVerification } from '../RSSVerification';
import { ReviewAndSubmit } from '../ReviewAndSubmit';
import { AnimatedReleasePreview } from '../PreviewPane';
import { AdditionalInfoForm } from '../AdditionalInfoForm';
import { Picker } from '../Picker';
import {
  selectFeedValidationStatus,
  selectFeedSubmissionStatus,
  selectCheckVerificationCodeStatus,
  selectSendVerificationEmailStatus,
  selectFeed,
  selectSubmittedFeed,
  selectCategories,
  selectCountries,
  selectLanguages,
  selectAggregators,
  selectAggregatorsState,
  selectCategoriesState,
  selectLanguagesState,
  selectCountriesState,
  selectNetworkMetadata,
  selectCurrentNetworkId,
  selectMetadataErrors,
  selectMe,
  LOADING,
  UNAUTHORIZED,
  CONFLICT,
  SUBMIT_SUCCESS,
} from '../../../store/selectors';
import {
  validateFeedAllowingExistingShows,
  submitFeed,
  resetFeed,
  updateFeed,
  unsetSubmitFeedStatus,
  getCategoryErrors,
} from '../../../store/podcastSubmission';
import { loadCountries, setCountry, initializeCountry } from '../../../store/countries';
import { loadLanguages, setLanguage, initializeLanguage } from '../../../store/languages';
import { loadAggregators, setAggregator, initializeAggregator } from '../../../store/aggregators';
import {
  loadCategories,
  initializeCategory,
  setCategory,
  setSubcategory,
  removeCategory,
  removeSubcategory,
} from '../../../store/categories';
import { loadNetworkMetadata } from '../../../store/networkMetadata';
import {
  sendVerificationEmail,
  checkVerificationCode,
  resetVerificationCodeStatus,
} from '../../../store/claimPodcast';
import { loadMe } from '../../../store/me';
import './index.module.scss';
import {
  Me,
  Country,
  Language,
  Category,
  SelectedCategory,
  Podcast,
  Network,
  Show,
  ReduxState,
  Subcategory,
  PreviewPanePodcast,
} from '../../../types';
import { ConfirmNewSubmission } from '../ConfirmNewSubmission';
import { AddPodcastModalContext } from './context';
import { injectIntl, IntlShape, defineMessage, FormattedMessage, defineMessages } from 'react-intl';
import { TSFix } from '@spotify-internal/ts-migration-utils/types';

const StyledNavStepper = styled(NavStepper)`
  @media (max-width: 480px) {
    display: none;
  }
`;

enum STEPS {
  RSS_VERIFICATION,
  DUPLICATE_PODCAST_SELECTOR,
  SUBMIT_NEW_PODCAST,
  CLAIM_PODCAST,
  ADDITIONAL_INFO,
  REVIEW_AND_SUBMIT,
}

interface Props {
  onClose(): void;
  validateFeedAllowingExistingShows(feedUrl: string): void;
  submitFeed(
    networkId: string,
    feedUrl: string,
    metadata: {
      countryOfOrigin: string;
      language: string;
      emailVerificationToken: string;
    },
  ): void;
  resetFeed(): void;
  updateFeed(
    showId: string,
    metadata: {
      countryOfOrigin: string;
      language: string;
      emailVerificationToken: string;
    },
  ): void;
  submittedFeedUri?: string;

  /**
   * We only have this after the user evaluates their feed url.
   */
  rssFeed?: Podcast;

  loadCategories(): void;
  loadMe(): void;
  loadCountries(): void;
  loadLanguages(): void;
  loadAggregators(): void;

  countries?: Country[];
  categories?: Category[];
  aggregators?: Network[];
  languages?: Language[];

  selectedCountry?: Country;
  selectedLanguage?: Language;
  selectedAggregator?: Network;
  selectedCategories?: SelectedCategory[];

  initializeCategory(key?: string): void;
  initializeCountry(): void;
  initializeLanguage(): void;
  initializeAggregator(): void;

  setCountry(value: Country): void;
  setLanguage(value: Language): void;
  setAggregator(value: Network): void;

  setCategory(key: string, category: { name: string; value: string }): void;
  setSubcategory(key: string, subcategory: Subcategory): void;
  removeCategory(key: string): void;
  removeSubcategory(key: string, subcategory: Subcategory): void;

  loadNetworkMetadata(id?: string): void;
  unsetSubmitFeedStatus(): void;

  validationStatus?: 'UNAUTHORIZED' | 'LOADING' | 'ERROR';
  networkMetadata?: {};

  me: Me;

  currentNetworkId?: string;
  submissionStatus?: 'UNAUTHORIZED' | 'LOADING' | 'ERROR' | 'CONFLICT' | 'SUBMIT_SUCCESS';
  checkVerificationCode(
    showId: string,
    verificationCode: string,
    claimExistingShow?: boolean,
  ): void;
  checkVerificationCodeStatus?: 'LOADING' | 'ERROR' | 'SUCCESS';
  resetVerificationCodeStatus(): void;
  sendVerificationEmail(showId: string, claimExistingShow?: boolean): void;
  sendVerificationEmailStatus?: 'ERROR' | 'LOADING' | 'SUCCESS';
  errors?: {
    country?: boolean;
    language?: boolean;
    category?: boolean[];
    aggregator?: boolean;
  };
  intl: IntlShape;
}

enum PreviewMode {
  PREVIEW_THIN = 'PREVIEW_THIN',
  PREVIEW_CELEBRATION = 'PREVIEW_CELEBRATION',
}

interface State {
  currentStep: STEPS;
  podcastRSSLink: string;
  emailVerificationToken: string;
  selectedShow: Show | null;
  metadataErrors?: MetadataErrors;
  previewPanePodcast: PreviewPanePodcast | null;
  nextButtonDisabled: boolean;
}

interface MetadataErrors {
  category: boolean[];
  country: boolean;
  language: boolean;
  aggregator?: boolean;
}

export class AddPodcastModal extends React.Component<Props & RouteComponentProps, State> {
  state: State = {
    currentStep: STEPS.RSS_VERIFICATION,
    podcastRSSLink: '',
    emailVerificationToken: '',
    selectedShow: null,
    metadataErrors: undefined,
    previewPanePodcast: null,
    nextButtonDisabled: false,
  };

  async componentDidMount() {
    await Promise.all([
      this.props.loadCategories(),
      this.props.loadCountries(),
      this.props.loadLanguages(),
      this.props.loadAggregators(),
    ]);

    /* istanbul ignore else */
    if (this.isNetworkUser) {
      this.props.loadNetworkMetadata(this.props.currentNetworkId);
    }
  }

  componentWillUnmount() {
    this.props.resetFeed();
  }

  get isNetworkUser() {
    return this.props.me && this.props.me.permissions.network;
  }

  get feedMetadata(): {
    countryOfOrigin: string;
    language: string;
    emailVerificationToken: string;
  } {
    /* Unsafe… */
    const countryOfOrigin = this.props.selectedCountry!.code!;
    const language = this.props.selectedLanguage!.code!;

    return {
      countryOfOrigin,
      language,
      emailVerificationToken: this.state.emailVerificationToken,
    };
  }

  shouldShowAggregators() {
    // only users with podcast-level access need to specify their aggregator (we already know the aggregator of network-level users)
    // and only show aggregator choice to new podcasts since if the user is claiming an existing podcast we can't currently update that metadata
    return Boolean(
      !this.props.me.permissions.network &&
        this.props.rssFeed &&
        !this.props.rssFeed.isExistingShow,
    );
  }

  get aggregators() {
    // istanbul ignore next
    if (!this.props.aggregators) {
      // istanbul ignore next
      return [];
    }

    const msg = defineMessage({
      id: 'add-podcast.other',
      description:
        'An option to select when the user does not know their podcasting hosting provider or it does not appear in the list',
      defaultMessage: "Other / I don't know",
    });

    return this.props.aggregators.map(row => {
      const newRow = { ...row };
      if (!row.name) newRow.name = this.props.intl.formatMessage(msg);
      return newRow;
    });
  }

  get userNetworkId() {
    if (this.props.selectedAggregator) {
      return this.props.selectedAggregator.id;
    }

    /* Unsafe, need to figure out if we actually have this. */
    return this.props.rssFeed!.network!.id;
  }

  handleOnClose = () => {
    this.props.onClose();
  };

  handlePreviousClick = () => {
    switch (this.state.currentStep) {
      case STEPS.DUPLICATE_PODCAST_SELECTOR:
        this.setState({ currentStep: STEPS.RSS_VERIFICATION });
        break;
      case STEPS.SUBMIT_NEW_PODCAST:
        this.setState({ currentStep: STEPS.DUPLICATE_PODCAST_SELECTOR });
        break;
      case STEPS.CLAIM_PODCAST:
        if (this.props.rssFeed!.isExistingShow) {
          this.setState({ currentStep: STEPS.DUPLICATE_PODCAST_SELECTOR });
        } else {
          this.setState({ currentStep: STEPS.RSS_VERIFICATION });
        }
        break;
      case STEPS.ADDITIONAL_INFO:
        this.setState({ currentStep: STEPS.CLAIM_PODCAST });
        break;
      case STEPS.REVIEW_AND_SUBMIT:
        this.setState({ currentStep: STEPS.ADDITIONAL_INFO });
        break;
    }
  };

  getMetadataErrors = (validateAggregator: boolean): MetadataErrors => {
    const { selectedCategories, selectedCountry, selectedLanguage } = this.props;

    const errors: {
      category: boolean[];
      country: boolean;
      language: boolean;
      aggregator?: boolean;
    } = {
      category: getCategoryErrors(selectedCategories as SelectedCategory[]),
      country: !(selectedCountry as Country).code,
      language: !(selectedLanguage as Language).code,
    };

    if (validateAggregator) {
      errors.aggregator = !this.props.selectedAggregator!.id;
    }

    return errors;
  };

  hasMetadataErrors = (errors: MetadataErrors) => {
    return (
      errors.country ||
      errors.language ||
      errors.aggregator ||
      (errors.category && errors.category.includes(true))
    );
  };

  handleNextClick = () => {
    switch (this.state.currentStep) {
      case STEPS.RSS_VERIFICATION:
        if (this.props.rssFeed!.isExistingShow) {
          this.setState({ currentStep: STEPS.DUPLICATE_PODCAST_SELECTOR });
        } else {
          this.setState({ currentStep: STEPS.CLAIM_PODCAST });
        }
        break;
      case STEPS.DUPLICATE_PODCAST_SELECTOR:
        this.setState({ currentStep: STEPS.CLAIM_PODCAST });
        break;
      case STEPS.SUBMIT_NEW_PODCAST:
        this.setState({ currentStep: STEPS.CLAIM_PODCAST });
        break;
      case STEPS.CLAIM_PODCAST:
        this.setState({ currentStep: STEPS.ADDITIONAL_INFO });
        break;
      case STEPS.ADDITIONAL_INFO:
        const metadataErrors = this.getMetadataErrors(this.shouldShowAggregators());
        this.setState({ metadataErrors });

        if (!this.hasMetadataErrors(metadataErrors)) {
          this.setState({ currentStep: STEPS.REVIEW_AND_SUBMIT });
        }
        break;
    }
  };

  handleSubmitClick = async () => {
    if (this.state.selectedShow) {
      this.props.updateFeed(this.state.selectedShow.id, this.feedMetadata);
    } else {
      this.props.submitFeed(this.userNetworkId!, this.props.rssFeed!.feedUrl, this.feedMetadata);
    }
  };

  handleOnChange = (field: 'country' | 'language' | 'aggregator' | 'podcastRSSLink') => (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    const value = event.target.value;

    switch (field) {
      case 'country':
        this.props.setCountry(JSON.parse(value));
        break;
      case 'language':
        this.props.setLanguage(JSON.parse(value));
        break;
      case 'aggregator':
        this.props.setAggregator(JSON.parse(value));
        break;
    }
  };

  handleRSSFeedChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const podcastRSSLink = event.target.value.trim();
    this.setState({ selectedShow: null, podcastRSSLink });
  };

  handleVerifcationSuccess = (emailVerificationToken: string) => {
    this.setState({ emailVerificationToken: emailVerificationToken });
  };

  handleNewSubmission = () => {
    this.setPreviewPaneContext(this.props.rssFeed!);
    this.setState({ selectedShow: null, currentStep: STEPS.SUBMIT_NEW_PODCAST });
  };

  setPreviewPaneContext = (rssFeed: PreviewPanePodcast | null) => {
    this.setState({ previewPanePodcast: rssFeed });
  };

  setNextButtonDisabled = (disabled: boolean) => {
    this.setState({ nextButtonDisabled: disabled });
  };

  renderModalBody = () => {
    const currentStep = this.state.currentStep;
    let Panel: React.ReactNode = null;

    switch (currentStep) {
      case STEPS.RSS_VERIFICATION: {
        Panel = (
          <RSSVerification
            onChange={this.handleRSSFeedChange}
            validateFeed={this.props.validateFeedAllowingExistingShows}
            podcastRSSLink={this.state.podcastRSSLink}
            rssFeed={this.props.rssFeed}
            network={this.props.networkMetadata}
            status={this.props.validationStatus}
          />
        );
        break;
      }
      case STEPS.DUPLICATE_PODCAST_SELECTOR: {
        const { selectedShow } = this.state;

        Panel = (
          <Picker
            selectedShowId={selectedShow && selectedShow.id}
            shows={this.props.rssFeed!.shows}
            handlePodcastSelection={(show: Show) => {
              this.setState({ selectedShow: show });
            }}
            handleNewSubmission={this.handleNewSubmission}
          />
        );
        break;
      }
      case STEPS.SUBMIT_NEW_PODCAST: {
        Panel = <ConfirmNewSubmission podcastRSSLink={this.state.podcastRSSLink} />;
        break;
      }
      case STEPS.CLAIM_PODCAST: {
        const { email } = this.props.rssFeed!;
        const { selectedShow } = this.state;

        Panel = (
          <ClaimPodcast
            redactedEmailAddress={selectedShow ? selectedShow.redactedEmailAddress : email}
            showId={selectedShow ? selectedShow.id : encodeURIComponent(this.state.podcastRSSLink)}
            onVerificationSuccess={this.handleVerifcationSuccess}
            claimExistingShow={!!selectedShow}
            sendVerificationEmail={this.props.sendVerificationEmail}
            sendVerificationEmailStatus={this.props.sendVerificationEmailStatus}
            checkVerificationCodeStatus={this.props.checkVerificationCodeStatus}
            checkVerificationCode={this.props.checkVerificationCode}
            resetVerificationCodeStatus={this.props.resetVerificationCodeStatus}
          />
        );
        break;
      }
      case STEPS.ADDITIONAL_INFO: {
        const initializers = {
          initializeCategory: this.props.initializeCategory,
          initializeCountry: this.props.initializeCountry,
          initializeLanguage: this.props.initializeLanguage,
          initializeAggregator: this.props.initializeAggregator,
        };

        Panel = (
          <AdditionalInfoForm
            intl={this.props.intl}
            shouldShowAggregators={this.shouldShowAggregators()}
            countries={this.props.countries}
            languages={this.props.languages}
            categories={this.props.categories}
            aggregators={this.aggregators}
            selectedLanguage={this.props.selectedLanguage}
            selectedCategories={this.props.selectedCategories}
            selectedCountry={this.props.selectedCountry}
            selectedAggregator={this.props.selectedAggregator}
            onChange={this.handleOnChange}
            errors={this.state.metadataErrors}
            setCategory={this.props.setCategory}
            setSubcategory={this.props.setSubcategory}
            removeCategory={this.props.removeCategory}
            removeSubcategory={this.props.removeSubcategory}
            {...initializers}
          />
        );
        break;
      }
      case STEPS.REVIEW_AND_SUBMIT: {
        Panel = (
          <ReviewAndSubmit
            shouldShowAggregators={this.shouldShowAggregators()}
            country={this.props.selectedCountry}
            language={this.props.selectedLanguage}
            aggregator={this.props.selectedAggregator}
            categories={this.props.selectedCategories}
            status={this.props.submissionStatus}
          />
        );
        break;
      }
    }

    return (
      <Fragment key="panel">
        {Panel}
        <AnimatedReleasePreview
          rssFeed={this.state.previewPanePodcast}
          previewMode={
            this.props.submissionStatus === SUBMIT_SUCCESS
              ? PreviewMode.PREVIEW_CELEBRATION
              : PreviewMode.PREVIEW_THIN
          }
          existingPodcast={!!this.state.selectedShow}
          history={this.props.history}
          location={this.props.location}
          unsetSubmitFeedStatus={this.props.unsetSubmitFeedStatus}
          loadMe={this.props.loadMe}
          submittedFeedUri={this.props.submittedFeedUri}
        />
      </Fragment>
    );
  };

  renderModalFooter = () => {
    const currentStep = this.state.currentStep;

    const cancelButton = (
      <ButtonTertiary key="close" onClick={this.handleOnClose}>
        <FormattedMessage
          id="add-podcast.cancel"
          description="Button label to exit the process of adding a podcast"
          defaultMessage="Cancel"
        />
      </ButtonTertiary>
    );
    const backButton = (
      <ButtonTertiary key="previous" data-qa="previous-button" onClick={this.handlePreviousClick}>
        <FormattedMessage
          id="add-podcast.go-back"
          description="Button label go back a step in the process of adding a podcast"
          defaultMessage="Go Back"
        />
      </ButtonTertiary>
    );
    const nextButton = (
      <ButtonPrimary
        key="next"
        data-qa="next-button"
        data-testid="next-button"
        onClick={this.handleNextClick}
        disabled={this.state.nextButtonDisabled}
      >
        <FormattedMessage
          id="add-podcast.next"
          description="Button label to go to the next step of the process of adding a podcast"
          defaultMessage="Next"
        />
      </ButtonPrimary>
    );
    const submitMessages = defineMessages({
      submit: {
        id: 'add.podcast.submit',
        description: 'Button label to submit a podcast at the end of the add podcast process',
        defaultMessage: 'Submit',
      },
      submitting: {
        id: 'add.podcast.submitting',
        description: 'Button label after ther user had clicked submit to add podcast',
        defaultMessage: 'Submitting...',
      },
    });
    const submitButton = (
      <ButtonPrimary
        data-qa="submit-button"
        key="submit"
        onClick={this.handleSubmitClick}
        disabled={
          this.props.submissionStatus === CONFLICT || this.props.submissionStatus === LOADING
        }
      >
        {this.props.submissionStatus === LOADING
          ? this.props.intl.formatMessage(submitMessages.submitting)
          : this.props.intl.formatMessage(submitMessages.submit)}
      </ButtonPrimary>
    );

    const footerButtons: { [key in STEPS]: [React.ReactNode, React.ReactNode] } = {
      [STEPS.RSS_VERIFICATION]: [cancelButton, nextButton],
      [STEPS.DUPLICATE_PODCAST_SELECTOR]: [backButton, nextButton],
      [STEPS.SUBMIT_NEW_PODCAST]: [backButton, nextButton],
      [STEPS.CLAIM_PODCAST]: [backButton, nextButton],
      [STEPS.ADDITIONAL_INFO]: [backButton, nextButton],
      [STEPS.REVIEW_AND_SUBMIT]: [backButton, submitButton],
    };

    return <div>{footerButtons[currentStep]}</div>;
  };

  render() {
    if (
      this.props.validationStatus === UNAUTHORIZED ||
      this.props.submissionStatus === UNAUTHORIZED
    ) {
      return <Redirect to="/no-access" />;
    }
    const { currentStep } = this.state;

    const dialogTitleMessages = defineMessages({
      claim: {
        id: 'add.podcast.dialog-title-claim',
        description: 'Title of dialog box when claiming access to an existing podcast on Spotify',
        defaultMessage: 'Claim your podcast',
      },
      add: {
        id: 'add.podcast.dialog-title-add',
        description: 'Title of dialog box when adding a new podcast to Spotify',
        defaultMessage: 'Add a podcast',
      },
    });

    const dialogNavStepMessages = defineMessages({
      verify: {
        id: 'add.podcast.dialog-step-verify',
        description: 'Step to verify ownership of this podcast',
        defaultMessage: 'Verify',
      },
      addInfo: {
        id: 'add.podcast.dialog-step-add-info',
        description: 'Step to add additional information about podcast',
        defaultMessage: 'Add podcast info',
      },
      reviewSubmit: {
        id: 'add.podcast.dialog-step-review-and-submit',
        description: 'Step to add review the information about a podcast and then submit',
        defaultMessage: 'Review and submit',
      },
    });

    return (
      <AddPodcastModalContext.Provider
        value={{
          previewPanePodcast: this.state.previewPanePodcast,
          setPreviewPanePodcast: this.setPreviewPaneContext,
          nextButtonDisabled: this.state.nextButtonDisabled,
          setNextButtonDisabled: this.setNextButtonDisabled,
        }}
      >
        <DialogFullScreen
          data-qa="add-podcast-overlay"
          data-testid="add-podcast-overlay"
          onClose={this.handleOnClose}
          dialogTitle={
            this.state.selectedShow
              ? this.props.intl.formatMessage(dialogTitleMessages.claim)
              : this.props.intl.formatMessage(dialogTitleMessages.add)
          }
          navStepper={
            <StyledNavStepper>
              <NavStepperItem
                visited={currentStep > STEPS.RSS_VERIFICATION}
                active={currentStep === STEPS.RSS_VERIFICATION}
              >
                {this.props.intl.formatMessage(dialogNavStepMessages.verify)}
              </NavStepperItem>
              <NavStepperItem
                visited={currentStep > STEPS.ADDITIONAL_INFO}
                active={currentStep === STEPS.ADDITIONAL_INFO}
              >
                {this.props.intl.formatMessage(dialogNavStepMessages.addInfo)}
              </NavStepperItem>
              <NavStepperItem
                visited={currentStep > STEPS.REVIEW_AND_SUBMIT}
                active={currentStep === STEPS.REVIEW_AND_SUBMIT}
              >
                {this.props.intl.formatMessage(dialogNavStepMessages.reviewSubmit)}
              </NavStepperItem>
            </StyledNavStepper>
          }
          body={this.renderModalBody()}
          bodyStyle={{ padding: '0' }}
          footer={this.renderModalFooter()}
        />
      </AddPodcastModalContext.Provider>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  validationStatus: selectFeedValidationStatus(state),
  submissionStatus: selectFeedSubmissionStatus(state),
  checkVerificationCodeStatus: selectCheckVerificationCodeStatus(state),
  sendVerificationEmailStatus: selectSendVerificationEmailStatus(state),
  rssFeed: selectFeed(state),
  submittedFeedUri: selectSubmittedFeed(state),
  categories: selectCategories(state),
  countries: selectCountries(state),
  languages: selectLanguages(state),
  aggregators: selectAggregators(state),
  selectedCategories: selectCategoriesState(state),
  selectedCountry: selectCountriesState(state),
  selectedLanguage: selectLanguagesState(state),
  selectedAggregator: selectAggregatorsState(state),
  me: selectMe(state)!,
  networkMetadata: selectNetworkMetadata(state),
  currentNetworkId: selectCurrentNetworkId(state),
  errors: selectMetadataErrors(state),
});

const actions = {
  validateFeedAllowingExistingShows,
  submitFeed,
  resetFeed,
  updateFeed,
  loadMe,
  loadCategories,
  loadLanguages,
  loadCountries,
  loadAggregators,
  loadNetworkMetadata,
  initializeCategory,
  setCategory,
  setAggregator,
  setSubcategory,
  removeCategory,
  removeSubcategory,
  unsetSubmitFeedStatus,
  setCountry,
  setLanguage,
  initializeCountry,
  initializeLanguage,
  initializeAggregator,
  sendVerificationEmail,
  checkVerificationCode,
  resetVerificationCodeStatus,
};

export const ConnectedAddPodcastModal = connect(
  mapStateToProps,
  actions,
)(injectIntl(AddPodcastModal as TSFix));
